import { CircularProgress, Container, Grid, SvgIcon, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import StoryContext from '../../contexts/StoryContext';
import { rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toFormattedNumber } from '../../modules/text';
import MessageBox from '../MessageBox';

function AccessInfographic() {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(null);
  const [subsidized, setSubsidized] = useState(null);
  const { primaryColor, secondaryColor } = useContext(StoryContext);

  useEffect(() => {
    // Instantiate a new server to define where the RDS API is hosted.
    // const server = new RdsServer(process.env.REACT_APP_RDS_SERVER);

    function getCapacity() {
      // Instantiate a catalog that exists on the server
      // const catalog = new RdsCatalog(server, 'ccdc');
      // Instantiate a data product that exists on the catalog
      // const dataProduct = new RdsDataProduct(catalog, 'provider');

      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
      whereClause += ' AND (setting_type=HOM OR setting_type=CEN) AND license_type=LIC';

      return rdsTabulate('ccdc', 'provider', {
        dims: 'is_subsidized',
        measure: 'count:COUNT(*)',
        where: whereClause,
        format: 'amcharts',
      });
    }

    setReady(false);
    setError(null);
    Promise.all([getCapacity()])
      .then((responses) => {
        if (responses[0].parsedBody.dataProvider.length > 0) {
          let totalCount = 0;
          let subsidizedCount = 0;
          responses[0].parsedBody.dataProvider.forEach((d) => {
            if (d.is_subsidized === '1') {
              subsidizedCount += d.count;
            }
            totalCount += d.count;
          });
          setTotal(totalCount);
          setSubsidized(subsidizedCount);
        } else {
          setError(new Error('No data returned'));
          setTotal(null);
          setSubsidized(null);
        }
      })
      .catch((rdsError) => {
        // console.error(rdsError);
        setError(rdsError);
      })
      .finally(() => {
        setReady(true);
      });
  }, [stateCode]);

  if (!ready) {
    return (
      <CircularProgress />
    );
  }

  return (
    <Grid container spacing={2} justify="center">
      <Grid item xs={10} sm={6}>
        {error && <Typography variant="body1" align="center" color="error">{error.message}</Typography>}
        <MessageBox>
          <Typography variant="body1" align="left">
            <b>{states[stateCode.toUpperCase()]}</b> has <b>{toFormattedNumber(total)}</b> licensed providers who care for and educate infants, toddlers, and preschoolers.
            Of every 100 licensed providers, <b>{toFormattedNumber((subsidized / total) * 100)}</b> accept subsidies.
          </Typography>
        </MessageBox>
      </Grid>
      <Grid item xs={12}>
        <Container style={{ textAlign: 'center' }}>
          <SvgIcon style={{ fontSize: '15em' }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="24.376 89.5 563.251 613">
              <defs>
                <linearGradient id="subsidyGradient" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={primaryColor} />
                  <stop offset={`${(subsidized / total) * 100}%`} stopColor={primaryColor} />
                  <stop offset={`${(subsidized / total) * 100 + 0.1}%`} stopColor={secondaryColor} />
                  <stop offset="100%" stopColor={secondaryColor} />
                </linearGradient>
              </defs>
              <g fill="url('#subsidyGradient')">
                <path d="M244.545,171.862h142.77c2.607,0,4.721,2.114,4.721,4.721v5.03c0,2.607-2.113,4.721-4.721,4.721h-142.77
	c-2.607,0-4.721-2.113-4.721-4.721v-5.03C239.824,173.976,241.938,171.862,244.545,171.862z" />
                <path d="M242.037,116.318c5.416,13.313,11.307,27.463,12.361,41.806c0.183,3,2.654,5.349,5.66,5.377h112.413
	c2.928-0.006,5.375-2.23,5.659-5.145c1.879-19,9.005-37.729,15.643-55.66l0.359-0.991c1.056-2.855-1.17-6.728-4.103-5.891
	c-2.14,0.905-4.161,2.067-6.021,3.46c-2.098,1.507-4.521,2.499-7.075,2.895h-0.476c-3.866,0.393-7.738-0.687-10.844-3.023
	c-3.355-2.649-7.439-4.21-11.706-4.477h-0.72c-3.044-0.005-6.059,0.585-8.876,1.736l-9.531,3.859
	c-3.667,1.505-7.617,2.194-11.577,2.02h-0.335c-1.938-0.134-3.853-0.51-5.698-1.119L298.97,95.3
	c-1.815-0.605-3.709-0.939-5.621-0.991h-0.064c-2.583-0.03-5.147,0.442-7.551,1.389l-11.101,4.258
	c-2.414,0.937-4.947,1.53-7.525,1.763c-5.509,0.429-11.051-0.005-16.426-1.287c-0.946-0.227-1.864-0.555-2.74-0.978
	c0,0-5.647-3.19-7.718-3.653c-3.036-0.682-5.223,3.139-4.052,6.02L242.037,116.318z" />
                <path d="M331.784,369.341c-3.119-2.438-6.55-4.45-10.2-5.981v24.852c9.039-1.526,13.562-5.454,13.57-11.783
	C335.174,373.675,333.933,371.063,331.784,369.341z" />
                <path d="M295.047,315.842c-0.079,2.742,1.033,5.383,3.049,7.242c2.949,2.447,6.265,4.414,9.827,5.827v-23.45
	C299.348,306.722,295.056,310.182,295.047,315.842z" />
                <path d="M398.841,201.023c-3.678-4.046-8.899-6.342-14.368-6.316h-0.077c-34.641,0-103.923,0-138.551,0l0,0
	c-5.53-0.008-10.803,2.329-14.51,6.432c-40.957,45.022-81.566,107.293-81.566,176.987c0,140.622,74.054,167.237,165.384,167.237
	s165.307-26.615,165.307-167.301C480.459,308.33,439.824,246.045,398.841,201.023z M353.742,400.405
	c-7.649,6.424-18.369,10.282-32.158,11.577v20.453h-13.661v-20.067c-16.233-0.334-30.473-3.198-42.719-8.593v-26.344
	c6.711,3.196,13.716,5.738,20.916,7.589c7.124,2,14.426,3.292,21.803,3.859v-30.923l-6.676-2.573
	c-13.181-5.197-22.515-10.835-28.003-16.915c-5.533-6.209-8.479-14.301-8.233-22.614c-0.179-9.275,4.014-18.094,11.32-23.81
	c7.564-6.217,18.095-9.995,31.592-11.333v-15.281h13.661v14.87c14.227,0.443,28.243,3.559,41.317,9.185l-9.378,23.36
	c-10.186-4.212-20.958-6.836-31.939-7.782v29.444c12.966,4.991,22.198,9.317,27.694,12.979c4.897,3.049,9.03,7.181,12.079,12.079
	c2.643,4.685,3.976,9.994,3.859,15.372C365.225,385.51,361.4,394,353.742,400.405z" />
                <path d="M43.774,534.814h38.925c10.713,0,19.398,8.686,19.398,19.398v116.04c0,10.714-8.685,19.398-19.398,19.398H43.774
	c-10.713,0-19.398-8.685-19.398-19.398v-116.04C24.376,543.5,33.061,534.814,43.774,534.814z" />
                <path d="M586.184,593.279c-3.563-7.319-12.863-12.787-25.56-8.709l-131.348,34.667c-2.65,0.717-5.059,2.135-6.973,4.104l0,0
	c-4.632,4.684-10.945,7.319-17.532,7.319h-92.089c-1.598-0.061-2.844-1.404-2.783-3.001c0.055-1.459,1.188-2.648,2.642-2.775h92.23
	c10.5-0.022,18.993-8.554,18.971-19.054c-0.003-1.282-0.136-2.562-0.396-3.817c-1.987-8.898-9.933-15.192-19.051-15.089
	l-84.37-3.858c-17.765-1.44-25.225-3.499-31.721-5.814c-34.551-12.336-52.804-23.771-85.966-30.203
	c-40.918-7.924-83.38-3.268-83.38-3.268c-5.146,0-9.365,3.653-9.365,8.117v95.961c-0.006,0.094-0.006,0.188,0,0.282l0,0v24.968
	c-0.143,4.78,3.372,8.886,8.117,9.48c29.585,3.512,44.237,3.949,113.892,10.856c47.594,4.734,87.535,4.336,134.319,4.026
	c18.009-0.115,24.672-1.286,33.805-4.836l171.507-72.948C587.084,613.513,589.862,600.83,586.184,593.279z" />
                <path d="M429.019,600.843c0.006,0.103,0.006,0.206,0,0.309l88.333-36.211c-0.224-1.171-0.564-2.317-1.017-3.421
	c-3.472-9.024-13.602-13.525-22.626-10.054c-0.729,0.28-1.438,0.609-2.123,0.984l-76.1,31.232
	C422.434,586.97,427.442,593.32,429.019,600.843z" />
                <path d="M428.35,613.513l125.366-32.158c-1.695-10.312-11.429-17.297-21.74-15.602c-1.425,0.234-2.818,0.631-4.153,1.182
	l-98.302,40.288C429.405,609.361,429.012,611.477,428.35,613.513z" />
                <path d="M244.545,171.862h142.77c2.607,0,4.721,2.114,4.721,4.721v5.03c0,2.607-2.113,4.721-4.721,4.721h-142.77
                c-2.607,0-4.721-2.113-4.721-4.721v-5.03C239.824,173.976,241.938,171.862,244.545,171.862z"/>
                <path d="M242.037,116.318c5.416,13.313,11.307,27.463,12.361,41.806c0.183,3,2.654,5.349,5.66,5.377h112.413
	c2.928-0.006,5.375-2.23,5.659-5.145c1.879-19,9.005-37.729,15.643-55.66l0.359-0.991c1.056-2.855-1.17-6.728-4.103-5.891
	c-2.14,0.905-4.161,2.067-6.021,3.46c-2.098,1.507-4.521,2.499-7.075,2.895h-0.476c-3.866,0.393-7.738-0.687-10.844-3.023
	c-3.355-2.649-7.439-4.21-11.706-4.477h-0.72c-3.044-0.005-6.059,0.585-8.876,1.736l-9.531,3.859
	c-3.667,1.505-7.617,2.194-11.577,2.02h-0.335c-1.938-0.134-3.853-0.51-5.698-1.119L298.97,95.3
	c-1.815-0.605-3.709-0.939-5.621-0.991h-0.064c-2.583-0.03-5.147,0.442-7.551,1.389l-11.101,4.258
	c-2.414,0.937-4.947,1.53-7.525,1.763c-5.509,0.429-11.051-0.005-16.426-1.287c-0.946-0.227-1.864-0.555-2.74-0.978
	c0,0-5.647-3.19-7.718-3.653c-3.036-0.682-5.223,3.139-4.052,6.02L242.037,116.318z" />
                <path d="M331.784,369.341c-3.119-2.438-6.55-4.45-10.2-5.981v24.852c9.039-1.526,13.562-5.454,13.57-11.783
	C335.174,373.675,333.933,371.063,331.784,369.341z" />
                <path d="M295.047,315.842c-0.079,2.742,1.033,5.383,3.049,7.242c2.949,2.447,6.265,4.414,9.827,5.827v-23.45
	C299.348,306.722,295.056,310.182,295.047,315.842z" />
                <path d="M398.841,201.023c-3.678-4.046-8.899-6.342-14.368-6.316h-0.077c-34.641,0-103.923,0-138.551,0l0,0
	c-5.53-0.008-10.803,2.329-14.51,6.432c-40.957,45.022-81.566,107.293-81.566,176.987c0,140.622,74.054,167.237,165.384,167.237
	s165.307-26.615,165.307-167.301C480.459,308.33,439.824,246.045,398.841,201.023z M353.742,400.405
	c-7.649,6.424-18.369,10.282-32.158,11.577v20.453h-13.661v-20.067c-16.233-0.334-30.473-3.198-42.719-8.593v-26.344
	c6.711,3.196,13.716,5.738,20.916,7.589c7.124,2,14.426,3.292,21.803,3.859v-30.923l-6.676-2.573
	c-13.181-5.197-22.515-10.835-28.003-16.915c-5.533-6.209-8.479-14.301-8.233-22.614c-0.179-9.275,4.014-18.094,11.32-23.81
	c7.564-6.217,18.095-9.995,31.592-11.333v-15.281h13.661v14.87c14.227,0.443,28.243,3.559,41.317,9.185l-9.378,23.36
	c-10.186-4.212-20.958-6.836-31.939-7.782v29.444c12.966,4.991,22.198,9.317,27.694,12.979c4.897,3.049,9.03,7.181,12.079,12.079
	c2.643,4.685,3.976,9.994,3.859,15.372C365.225,385.51,361.4,394,353.742,400.405z" />
                <path d="M43.774,534.814h38.925c10.713,0,19.398,8.686,19.398,19.398v116.04c0,10.714-8.685,19.398-19.398,19.398H43.774
	c-10.713,0-19.398-8.685-19.398-19.398v-116.04C24.376,543.5,33.061,534.814,43.774,534.814z" />
                <path d="M586.184,593.279c-3.563-7.319-12.863-12.787-25.56-8.709l-131.348,34.667c-2.65,0.717-5.059,2.135-6.973,4.104l0,0
	c-4.632,4.684-10.945,7.319-17.532,7.319h-92.089c-1.598-0.061-2.844-1.404-2.783-3.001c0.055-1.459,1.188-2.648,2.642-2.775h92.23
	c10.5-0.022,18.993-8.554,18.971-19.054c-0.003-1.282-0.136-2.562-0.396-3.817c-1.987-8.898-9.933-15.192-19.051-15.089
	l-84.37-3.858c-17.765-1.44-25.225-3.499-31.721-5.814c-34.551-12.336-52.804-23.771-85.966-30.203
	c-40.918-7.924-83.38-3.268-83.38-3.268c-5.146,0-9.365,3.653-9.365,8.117v95.961c-0.006,0.094-0.006,0.188,0,0.282l0,0v24.968
	c-0.143,4.78,3.372,8.886,8.117,9.48c29.585,3.512,44.237,3.949,113.892,10.856c47.594,4.734,87.535,4.336,134.319,4.026
	c18.009-0.115,24.672-1.286,33.805-4.836l171.507-72.948C587.084,613.513,589.862,600.83,586.184,593.279z" />
                <path d="M429.019,600.843c0.006,0.103,0.006,0.206,0,0.309l88.333-36.211c-0.224-1.171-0.564-2.317-1.017-3.421
	c-3.472-9.024-13.602-13.525-22.626-10.054c-0.729,0.28-1.438,0.609-2.123,0.984l-76.1,31.232
	C422.434,586.97,427.442,593.32,429.019,600.843z" />
                <path d="M428.35,613.513l125.366-32.158c-1.695-10.312-11.429-17.297-21.74-15.602c-1.425,0.234-2.818,0.631-4.153,1.182
	l-98.302,40.288C429.405,609.361,429.012,611.477,428.35,613.513z" />
              </g>
            </svg>
          </SvgIcon>
        </Container>
      </Grid>
    </Grid>
  );
}

export default AccessInfographic;
